/*
 *
 * App
 *
 */

import React, { Suspense } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
// import { FormattedMessage } from 'react-intl';

import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';

import { createFetchPlatformConfigRequest } from './actions';
import reducer from './reducer';
import saga from './sagas';
import { selectHasFetchedPlatformConfig } from './selectors';
// import messages from './messages';

import { getDevTokenFromStorage } from 'cb-utils/devToken';
import LoadingIndicator from 'components/LoadingIndicator';
import LoginPage from 'components/LoginPage/Loadable';
import ProtectedRoute from 'components/ProtectedRoute';
import RegisterPage from 'components/RegisterPage/Loadable';
import TwoFactorAuthValidationPage from 'components/TwoFactorAuthValidationPage';
import CbPortal from 'containers/CbPortal/Loadable';
import Console from 'containers/Console/Loadable';
import LandingPage from 'containers/LandingPage/Loadable';
import NotFound from 'containers/NotFoundPage/Loadable';
import { OIDCCallbackPage, OIDCTokenPage } from 'features/oidc';
import { RouteComponentProps, withRouter } from 'react-router';
import { Redirect, Route, Switch } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import ConsoleRoutes from '../Console/routes';
import { STORE_KEY } from './constants';
import { AppRoutes } from './routes';

type AppProps = RouteComponentProps<{}>;

export class App extends React.Component<AppProps & StateToProps & DispatchToProps, {}> {
  hasLoaded = false;

  componentDidMount() {
    this.props.init();
  }

  shouldComponentUpdate(nextProps: AppProps) {
    const shouldUpdate = nextProps.location.pathname !== this.props.location.pathname || !this.hasLoaded;
    if (!this.hasLoaded) {
      this.hasLoaded = true;
    }
    return shouldUpdate;
  }

  render() {
    if (this.props.hasFetchedPlatformConfig) {
      return (
        // Suspense around entire app in case any children don't have their own and use React.lazy
        <Suspense fallback={<LoadingIndicator />}>
          <Switch>
            <Route path={AppRoutes.PORTAL_ROUTE} component={CbPortal} />
            <Route exact path={AppRoutes.DEV_2FA_VALIDATION_ROUTE} component={TwoFactorAuthValidationPage} />
            <Route exact path={AppRoutes.OIDC_CALLBACK_ROUTE} component={OIDCCallbackPage} />
            <Route exact path={AppRoutes.OIDC_TOKEN_ROUTE} component={OIDCTokenPage} />
            <Route path={AppRoutes.LOGIN_ROUTE} component={LoginPage} />
            <Route path={AppRoutes.REGISTER_ROUTE} component={RegisterPage} />
            {/* so we will have access to system and edge params everywhere*/}
            <ProtectedRoute path={AppRoutes.CONSOLE_SYSTEM_ROUTE} component={Console} />
            <ProtectedRoute path={AppRoutes.CONSOLE_BASE_ROUTE} component={Console} />
            <Route
              exact
              path="/"
              render={() =>
                getDevTokenFromStorage() ? <Redirect to={ConsoleRoutes.SYSTEM_OVERVIEW} /> : <LandingPage />
              }
            />
            <Route path="*" component={NotFound} />
          </Switch>
        </Suspense>
      );
    } else {
      return <LoadingIndicator />;
    }
  }
}

type StateToProps = ReturnType<typeof mapStateToProps>;
const mapStateToProps = createStructuredSelector({
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  hasFetchedPlatformConfig: selectHasFetchedPlatformConfig(),
});
type DispatchToProps = typeof mapDispatchToProps;

const mapDispatchToProps = {
  init: createFetchPlatformConfigRequest,
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const withReducer = injectReducer({ key: STORE_KEY, reducer });
const withSaga = injectSaga({ key: STORE_KEY, saga });

export default compose(withRouter, withReducer, withSaga, withConnect)(App);
